import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Search, Archive, RotateCcw, Check, X, Pencil, Inbox } from 'lucide-react';
import { campiAPI } from '../services/api';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import '../styles/Catalogo.css';

/**
 * CatalogoCampi — il catalogo dei campi clinici, lato amministrazione.
 *
 * COSA SI PUO' FARE E COSA NO, E PERCHE' SI VEDE
 * Un campo gia' usato in una scheda attiva non cambia significato: nome a
 * schermo si', tipo e unita' di misura no. Il divieto sta sul database
 * (un trigger), non qui: questa schermata lo ANTICIPA disabilitando i
 * campi, perche' scoprirlo dopo aver riscritto tutto e' peggio che
 * saperlo prima. Se la schermata sbagliasse, il server rifiuterebbe
 * comunque.
 *
 * LE PROPOSTE DEI MEDICI
 * Un medico che non trova un campo lo chiede. Qui si vedono le richieste
 * con chi le ha fatte e perche'; approvando si assegna il codice
 * definitivo e si completa la definizione, perche' quello che arriva e'
 * una richiesta, non una definizione.
 */

const TIPI = [
    ['numero', 'Numero'], ['testo', 'Testo'], ['testo_lungo', 'Testo lungo'],
    ['scelta_singola', 'Scelta'], ['scelta_multipla', 'Scelta multipla'],
    ['data', 'Data'], ['spunta', 'Spunta'],
];
const NOME_TIPO = Object.fromEntries(TIPI);
const VUOTO = {
    codice: '', nome: '', area_id: '', tipo: 'testo', unita: '', decimali: '',
    min_plausibile: '', max_plausibile: '',
    scala_id: '', aiuto: '',
};

const CatalogoCampi = () => {
    const [aree, setAree] = useState([]);
    const [scale, setScale] = useState([]);
    const [campi, setCampi] = useState([]);
    const [proposte, setProposte] = useState([]);
    const [area, setArea] = useState('');
    const [stato, setStato] = useState('attivo');
    const [cerca, setCerca] = useState('');
    const [form, setForm] = useState(null);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    /* Il riquadro chiede conferma se si chiude con qualcosa di diverso da
       com'era: velo e «Annulla» passano da chiudi(). L'istantanea si
       prende in apri(); il salvataggio chiude da se'. */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco(form, versione);
    const chiedi = useUscitaProtetta(sporco);
    const chiudi = () => { if (chiedi()) setForm(null); };
    const [caricando, setCaricando] = useState(true);

    /* La lettura riprova da sola una volta se la risposta non arriva (il
       fermo di un rilascio di giorno), poi dice il motivo con «Riprova»
       (primaLettura.js). `letturaFallita` distingue questo errore da quelli
       di un salvataggio, che non si riprovano. */
    const [letturaFallita, setLetturaFallita] = useState(false);
    const fallitaPrima = useRef(false);
    const carica = useCallback(async () => {
        try {
            const r = await conUnTentativo(
                () => campiAPI.elenco({ area: area || undefined, stato, cerca: cerca || undefined }));
            setCampi(r.data.data.campi);
            /* Tolto solo l'errore della lettura, non quello di un salvataggio. */
            if (fallitaPrima.current) { fallitaPrima.current = false; setErrore(null); setLetturaFallita(false); }
        } catch (e) {
            setErrore(motivoDellaLettura(e, 'Non riesco a leggere il catalogo'));
            fallitaPrima.current = true;
            setLetturaFallita(true);
        }
        finally { setCaricando(false); }
    }, [area, stato, cerca]);

    /* Aree e scale servono ai filtri e al modulo: anche loro riprovano una
       volta, e «Riprova» le rilegge se non erano arrivate. */
    const caricaElenchi = useCallback(async () => {
        try {
            const [a, s, p] = await Promise.all([
                conUnTentativo(() => campiAPI.aree()), conUnTentativo(() => campiAPI.scale()),
                conUnTentativo(() => campiAPI.proposte()),
            ]);
            setAree(a.data.data.aree);
            setScale(s.data.data.scale);
            setProposte(p.data.data.proposte);
        } catch (e) { /* le proposte possono mancare: non e' un errore */ }
    }, []);
    useEffect(() => { caricaElenchi(); }, [caricaElenchi]);
    const riprova = () => {
        setErrore(null);
        setLetturaFallita(false);
        fallitaPrima.current = false;
        setCaricando(true);
        if (!aree.length) caricaElenchi();
        carica();
    };

    useEffect(() => { const t = setTimeout(carica, 250); return () => clearTimeout(t); }, [carica]);

    const apri = (c) => {
        setErrore(null);
        setVersione((v) => v + 1);
        setForm(c ? {
            id: c.id, codice: c.codice, nome: c.nome,
            area_id: aree.find((a) => a.codice === c.area)?.id || '',
            tipo: c.tipo, unita: c.unita || '', decimali: c.decimali ?? '',
            min_plausibile: c.min_plausibile ?? '', max_plausibile: c.max_plausibile ?? '',
            scala_id: scale.find((s) => s.codice === c.scala)?.id || '',
            aiuto: c.aiuto || '', congelato: c.congelato, in_schede: Number(c.in_schede),
        } : { ...VUOTO, congelato: false, in_schede: 0 });
    };

    const salva = async (e) => {
        e.preventDefault();
        setErrore(null);
        const d = { ...form };
        delete d.congelato; delete d.in_schede;
        if (d.tipo !== 'numero') {
            d.unita = null; d.decimali = null;
            d.min_plausibile = null; d.max_plausibile = null;
        }
        if (!['scelta_singola', 'scelta_multipla'].includes(d.tipo)) d.scala_id = null;
        try {
            if (d.id) { const id = d.id; delete d.id; delete d.codice; await campiAPI.modifica(id, d); }
            else await campiAPI.crea(d);
            setForm(null); setAvviso(null); carica();
        } catch (err) {
            setErrore(err?.response?.data?.message || 'Non riesco a salvare');
        }
    };

    const ritira = async (c) => {
        if (!window.confirm(`Ritirare «${c.nome}»?\n\nNon si cancella: resta nelle schede in cui `
                          + 'compare gia\', e le compilazioni passate continuano a nominarlo. '
                          + 'Semplicemente non si aggiunge piu\' a schede nuove.')) return;
        try { const r = await campiAPI.ritira(c.id); setAvviso(r.data.message); carica(); }
        catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a ritirare il campo'); }
    };

    const decidi = async (p, approvata) => {
        setErrore(null);
        let codice;
        if (approvata) {
            codice = window.prompt(
                `Approvi «${p.nome}».\n\nIl codice non si cambia piu\': e\' la chiave con cui una `
              + 'compilazione di oggi verra\' riletta fra dieci anni.\n\n'
              + 'Maiuscole, cifre e trattino basso.', '');
            if (!codice) return;
        } else if (!window.confirm(`Rifiutare la richiesta di «${p.nome}»?`)) return;
        try {
            await campiAPI.decidi(p.id, { approvata, codice });
            const r = await campiAPI.proposte();
            setProposte(r.data.data.proposte);
            carica();
        } catch (e) { setErrore(e?.response?.data?.message || 'Non riesco a decidere'); }
    };

    const numerico = form?.tipo === 'numero';
    const aScelta = ['scelta_singola', 'scelta_multipla'].includes(form?.tipo);

    return (
        <div className="cat-pagina">
            <header className="cat-testa">
                <div>
                    <h1>Catalogo dei campi clinici</h1>
                    <p>Un campo si definisce una volta sola: nome, tipo, unità di misura.
                       Le schede lo pescano da qui, e non ne definiscono di propri.</p>
                </div>
                <button className="btn-primario" onClick={() => apri(null)}>
                    <Plus size={16} aria-hidden="true" /> Campo nuovo
                </button>
            </header>

            {avviso && <p className="cat-avviso">{avviso} <button onClick={() => setAvviso(null)}>×</button></p>}
            {errore && !form && (
                <p className="cat-errore">
                    {errore}
                    {letturaFallita && (
                        <> <button type="button" className="cat-riprova" onClick={riprova}>Riprova</button></>
                    )}
                </p>
            )}

            {proposte.length > 0 && (
                <section className="cat-proposte">
                    <h2><Inbox size={17} aria-hidden="true" /> Richieste dei medici ({proposte.length})</h2>
                    <p className="cat-sotto">Un campo proposto non esiste per nessun altro finché non
                       lo approvi. Approvandolo gli assegni il codice definitivo.</p>
                    {proposte.map((p) => (
                        <div key={p.id} className="cat-proposta">
                            <div>
                                <strong>{p.nome}</strong>
                                <span className="cat-meta">
                                    {NOME_TIPO[p.tipo]}{p.unita ? ` · ${p.unita}` : ''} · {p.area_nome}
                                    {p.per_scheda ? ` · per «${p.per_scheda}»` : ''}
                                </span>
                                <p className="cat-motivo">«{p.proposto_motivo}»</p>
                                <span className="cat-meta">chiesto da {p.chi}</span>
                            </div>
                            <div className="cat-azioni">
                                <button className="btn-si" onClick={() => decidi(p, true)}>
                                    <Check size={15} aria-hidden="true" /> Approva
                                </button>
                                <button className="btn-no" onClick={() => decidi(p, false)}>
                                    <X size={15} aria-hidden="true" /> Rifiuta
                                </button>
                            </div>
                        </div>
                    ))}
                </section>
            )}

            <div className="cat-filtri" data-non-trattiene>
                <label className="cat-cerca">
                    <Search size={15} aria-hidden="true" />
                    <input value={cerca} onChange={(e) => setCerca(e.target.value)}
                           placeholder="Cerca per nome o codice" />
                </label>
                <select value={area} onChange={(e) => setArea(e.target.value)}>
                    <option value="">Tutte le aree</option>
                    {aree.map((a) => (
                        <option key={a.id} value={a.codice}>
                            {a.nome}{a.trasversale ? ' (trasversale)' : ''} — {a.campi}
                        </option>
                    ))}
                </select>
                <select value={stato} onChange={(e) => setStato(e.target.value)}>
                    <option value="attivo">In catalogo</option>
                    <option value="ritirato">Ritirati</option>
                    <option value="tutti">Tutti</option>
                </select>
                <span className="cat-conteggio">{campi.length} campi</span>
            </div>

            {caricando ? <p className="cat-attesa">Carico…</p> : campi.length === 0
                ? <p className="cat-vuoto">Nessun campo con questi filtri.</p> : (
                <div className="cat-tabella rm-scorre">
                    <table>
                        <thead>
                            <tr>
                                <th>Campo</th><th>Area</th><th>Tipo</th><th>Unità</th>
                                <th>Limiti</th><th>Usato in</th><th />
                            </tr>
                        </thead>
                        <tbody>
                            {campi.map((c) => (
                                <tr key={c.id} className={c.stato === 'ritirato' ? 'cat-ritirato' : ''}>
                                    <td>
                                        <strong>{c.nome}</strong>
                                        <span className="cat-codice">{c.codice}</span>
                                        {c.formula && <span className="cat-tag">calcolato</span>}
                                        {c.da_anagrafica && <span className="cat-tag">dall’anagrafica</span>}
                                        {c.analita && <span className="cat-tag">esame di laboratorio</span>}
                                    </td>
                                    <td>{c.area_nome}</td>
                                    <td>{NOME_TIPO[c.tipo] || c.tipo}</td>
                                    <td className="cat-num">{c.unita || c.analita_unita || '—'}</td>
                                    <td className="cat-num">
                                        {c.min_plausibile == null && c.max_plausibile == null ? '—'
                                          : `${c.min_plausibile ?? '−∞'} – ${c.max_plausibile ?? '+∞'}`}
                                    </td>
                                    <td className="cat-num">
                                        {Number(c.in_schede) || '—'}
                                        {c.congelato && <span className="cat-lucchetto" title={
                                            'In una scheda attiva: si può cambiare il nome a schermo, '
                                          + 'non tipo, unità o limiti.'}>bloccato</span>}
                                    </td>
                                    <td className="cat-fine">
                                        <button className="btn-icona" title="Modifica" onClick={() => apri(c)}>
                                            <Pencil size={15} aria-hidden="true" />
                                        </button>
                                        {c.stato === 'attivo' ? (
                                            <button className="btn-icona" title="Ritira" onClick={() => ritira(c)}>
                                                <Archive size={15} aria-hidden="true" />
                                            </button>
                                        ) : (
                                            <button className="btn-icona" title="Rimetti in catalogo"
                                                    onClick={async () => { await campiAPI.riattiva(c.id); carica(); }}>
                                                <RotateCcw size={15} aria-hidden="true" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {form && (
                <div className="cat-velo" onClick={(e) => { if (e.target === e.currentTarget) chiudi(); }}>
                    <form className="cat-riquadro" onSubmit={salva}>
                        <h2>{form.id ? 'Modifica campo' : 'Campo nuovo'}</h2>

                        {form.congelato && (
                            <p className="cat-bloccato">
                                Questo campo è in <strong>{form.in_schede} schede</strong>, di cui almeno una
                                attiva. Puoi cambiare il nome a schermo e l’aiuto: tipo, unità e limiti no,
                                perché le compilazioni già fatte diventerebbero illeggibili senza che
                                nessuno se ne accorga. Per cambiarli si ritira questo campo e se ne fa uno nuovo.
                            </p>
                        )}
                        {errore && <p className="cat-errore">{errore}</p>}

                        <div className="cat-riga">
                            <label>
                                Codice
                                <input value={form.codice} disabled={!!form.id} required
                                       onChange={(e) => setForm({ ...form, codice: e.target.value.toUpperCase() })}
                                       placeholder="ORT_DOLORE_NOTTURNO" />
                                <small>{form.id ? 'Non si cambia: è la chiave con cui le compilazioni vecchie lo ritrovano.'
                                               : 'Maiuscole, cifre e trattino basso. Non si cambierà più.'}</small>
                            </label>
                            <label>
                                Nome a schermo
                                <input value={form.nome} required
                                       onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                            </label>
                        </div>

                        <div className="cat-riga">
                            <label>
                                Area
                                <select value={form.area_id} required
                                        onChange={(e) => setForm({ ...form, area_id: e.target.value })}>
                                    <option value="">—</option>
                                    {aree.map((a) => <option key={a.id} value={a.id}>
                                        {a.nome}{a.trasversale ? ' (trasversale)' : ''}</option>)}
                                </select>
                            </label>
                            <label>
                                Tipo
                                <select value={form.tipo} disabled={form.congelato}
                                        onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                                    {TIPI.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
                                </select>
                            </label>
                        </div>

                        {numerico && (
                            <div className="cat-riga">
                                <label>
                                    Unità di misura
                                    <input value={form.unita} disabled={form.congelato}
                                           onChange={(e) => setForm({ ...form, unita: e.target.value })}
                                           placeholder="kg, cm, mmHg…" />
                                </label>
                                <label>
                                    Decimali
                                    <input type="number" min="0" max="4" value={form.decimali} disabled={form.congelato}
                                           onChange={(e) => setForm({ ...form, decimali: e.target.value })} />
                                </label>
                                <label>
                                    Minimo plausibile
                                    <input type="number" step="any" value={form.min_plausibile} disabled={form.congelato}
                                           onChange={(e) => setForm({ ...form, min_plausibile: e.target.value })} />
                                </label>
                                <label>
                                    Massimo plausibile
                                    <input type="number" step="any" value={form.max_plausibile} disabled={form.congelato}
                                           onChange={(e) => setForm({ ...form, max_plausibile: e.target.value })} />
                                </label>
                            </div>
                        )}
                        {/* NIENTE INTERVALLO DI RIFERIMENTO, dal 21 settembre 2026. C'era,
                            con due caselle qui e la segnalazione «fuori riferimento»
                            sulla scheda: un software che confronta un valore del paziente
                            con un intervallo di normalita' e ne segnala lo scostamento
                            fornisce informazioni per una decisione clinica (MDR, regola
                            11), e RIMEDICHUB registra, non giudica. Le due colonne non
                            esistono piu' nel database (migration 195), e dal 6 ottobre
                            2026 il server rifiuta con un 400 la richiesta che ne porta
                            una, invece di ignorarla. Il giudizio sul valore e' del
                            medico. */}
                        {numerico && (
                            <p className="cat-nota">
                                <strong>I limiti dicono cosa è impossibile, non cosa è normale.</strong> Un
                                peso di 900 kg è un errore di battitura e il gestionale lo rifiuta. Un
                                valore dentro i limiti non è «normale»: il gestionale non ha intervalli
                                di riferimento e non segnala scostamenti — registra, e il giudizio è
                                del medico.
                            </p>
                        )}

                        {aScelta && (
                            <label>
                                Risposte ammesse
                                <select value={form.scala_id} required disabled={form.congelato}
                                        onChange={(e) => setForm({ ...form, scala_id: e.target.value })}>
                                    <option value="">—</option>
                                    {scale.map((s) => <option key={s.id} value={s.id}>
                                        {s.nome} ({s.opzioni.length})</option>)}
                                </select>
                                <small>Le scale si condividono: «Assente / Lieve / Moderato / Grave» è la
                                       stessa su tutte le schede che la usano.</small>
                            </label>
                        )}

                        <label>
                            Aiuto per chi compila
                            <textarea rows={3} value={form.aiuto}
                                      onChange={(e) => setForm({ ...form, aiuto: e.target.value })}
                                      placeholder="Come si misura, dove si legge la soglia dello strumento, cosa non è." />
                        </label>

                        <div className="cat-pulsanti">
                            <button type="button" className="btn-annulla" onClick={chiudi}>Annulla</button>
                            <button type="submit" className="btn-primario">Salva</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default CatalogoCampi;
